/**
 * Экран AI-торговли: выбор пары и ставки → «скан» → серия Smart Scaling → результат.
 * Визуал по design-spec §7 (AI): Signal Setup, analysis-card, direction-card.
 * Данные и исход считает сервер (`POST /api/trade/cycle`) — фронт только рисует.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, serverError } from '../lib/api.js';
import type { Asset, CycleResult, SettingsPayload, TradeStep } from '../lib/api.js';
import { haptic } from '../lib/telegram.js';
import { money } from '../lib/format.js';
import { useApp } from '../state/app.js';

type Phase = 'setup' | 'analyzing' | 'trading' | 'result';

const STEP_DELAY_MS = 900;

export function TradeScreen() {
  const { me, patchMe, showToast, setTab, t } = useApp();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [pair, setPair] = useState('EURUSD');
  const [amount, setAmount] = useState(5);
  const [phase, setPhase] = useState<Phase>('setup');
  const [visibleSteps, setVisibleSteps] = useState<TradeStep[]>([]);
  const [result, setResult] = useState<CycleResult | null>(null);

  useEffect(() => {
    void api
      .assets()
      .then(({ assets: list }) => {
        setAssets(list);
        if (list[0]) setPair(list[0].symbol);
      })
      .catch(() => undefined);
    void api.settings().then(setSettings).catch(() => undefined);
  }, []);

  /** Шаги показываем по одному — эффект живой торговли (BOT_SPEC §6). */
  useEffect(() => {
    if (phase !== 'trading' || !result) return;
    if (visibleSteps.length >= result.steps.length) {
      const timer = globalThis.setTimeout(() => setPhase('result'), STEP_DELAY_MS);
      return () => globalThis.clearTimeout(timer);
    }
    const timer = globalThis.setTimeout(() => {
      setVisibleSteps(result.steps.slice(0, visibleSteps.length + 1));
      haptic('light');
    }, STEP_DELAY_MS);
    return () => globalThis.clearTimeout(timer);
  }, [phase, result, visibleSteps.length]);

  async function launch() {
    haptic('medium');
    setPhase('analyzing');
    setVisibleSteps([]);
    setResult(null);

    try {
      const cycle = await api.runCycle({ amount, pair });
      // короткая пауза «анализа», как на экране бота
      globalThis.setTimeout(() => {
        setResult(cycle);
        setPhase('trading');
      }, 1400);

      if (me) {
        patchMe({
          user: { ...me.user, tokens: cycle.tokensLeft },
          balances: { ...me.balances, demo: cycle.balance },
        });
      }
    } catch (err) {
      setPhase('setup');
      if (err instanceof ApiError && err.isOutOfTokens) {
        showToast(t('app.trade.no_tokens'));
        setTab('tokens');
        return;
      }
      showToast(serverError(err) ?? t('app.trade.failed'));
    }
  }

  const bets = settings?.options.bets ?? [{ amount: 1 }, { amount: 3 }, { amount: 5 }, { amount: 10, risk: true }, { amount: 20, risk: true }];
  const strategy = settings?.current;

  return (
    <div className="screen">
      {phase === 'setup' && (
        <>
          <section className="card card-lg">
            <div className="ai-head">
              <img className="ai-emblem" src="/icons/trade.svg" alt="" width={64} height={64} />
              <div>
                <span className="kicker">Quantum AI</span>
                <h2 className="ai-title">{t('app.trade.setup_title')}</h2>
              </div>
            </div>

            <div className="stack">
              <div>
                <div className="label">{t('app.trade.pair')}</div>
                <div className="chip-row">
                  {(assets.length > 0 ? assets : [{ symbol: 'EURUSD', label: 'EUR/USD' } as Asset]).map(
                    (asset) => (
                      <button
                        key={asset.symbol}
                        type="button"
                        className={`chip ${pair === asset.symbol ? 'is-active' : ''}`}
                        onClick={() => {
                          haptic();
                          setPair(asset.symbol);
                        }}
                      >
                        {asset.label ?? asset.symbol}
                      </button>
                    ),
                  )}
                </div>
              </div>

              <div>
                <div className="label">{t('app.trade.amount')}</div>
                <div className="chip-row">
                  {bets.map((bet) => (
                    <button
                      key={bet.amount}
                      type="button"
                      className={`chip ${amount === bet.amount ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setAmount(bet.amount);
                      }}
                    >
                      ${bet.amount}
                      {bet.risk ? ' RISK' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {strategy && (
              <div className="ai-strategy-note">
                {t('app.trade.strategy_line', {
                  strategy: strategy.strategy,
                  progression: strategy.progression,
                  maxSteps: strategy.maxSteps,
                  expiration: strategy.expirationSeconds,
                })}
              </div>
            )}

            <button type="button" className="cta cta-launch cta-block" onClick={() => void launch()}>
              {t('app.trade.generate')}
            </button>
          </section>

          <section className="card">
            <div className="row-between">
              <span className="label">{t('app.trade.tokens_available')}</span>
              <span className="pill">{me?.user.tokens ?? 0} 💎</span>
            </div>
            <p className="body">{t('app.trade.cycle_note')}</p>
          </section>
        </>
      )}

      {phase === 'analyzing' && (
        <section className="card card-lg ai-analysis">
          <h2 className="ai-title ai-analysis-title">{t('app.trade.analyzing')}</h2>
          <div className="ai-analysis-rows">
            {[t('app.trade.scan_quotes'), 'RSI · MACD', 'MA50 · MA200', t('app.trade.scan_pattern')].map((row) => (
              <div key={row} className="ai-analysis-row">
                <span>{row}</span>
                <span className="ai-shimmer" />
              </div>
            ))}
          </div>
        </section>
      )}

      {(phase === 'trading' || phase === 'result') && result && (
        <>
          <section className={`card card-lg direction-card ${result.won ? 'is-up' : 'is-down'}`}>
            <span className="kicker">{result.pair}</span>
            <div className="value-xl">{result.steps[0]?.action === 'call' ? 'LONG' : 'SHORT'}</div>
            <span className="pill pill-green">{t('app.trade.smart_scaling')}</span>
          </section>

          <section className="card">
            <div className="steps-list">
              {visibleSteps.map((step) => (
                <div key={step.index} className={`step-row ${step.won ? 'is-win' : 'is-loss'}`}>
                  <span className="step-index">{t('app.trade.step', { index: step.index })}</span>
                  <span className="step-amount">${money(step.amount)}</span>
                  <span className="step-profit">
                    {step.profit > 0 ? '+' : '−'}${money(Math.abs(step.profit))}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {phase === 'result' && result && (
        <section className="card card-lg victory-card">
          <h2 className="card-title">{result.won ? t('app.trade.win_title') : t('app.trade.loss_title')}</h2>
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.trade.result')}</div>
              <div className={`victory-value ${result.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {result.profit >= 0 ? '+' : '−'}${money(Math.abs(result.profit))}
              </div>
            </div>
            <div>
              <div className="label">{t('app.trade.balance')}</div>
              <div className="victory-value">${money(result.balance)}</div>
            </div>
            <div>
              <div className="label">{t('app.trade.tokens_spent')}</div>
              <div className="victory-value">{result.tokensSpent} 💎</div>
            </div>
          </div>

          <div className="row">
            <button
              type="button"
              className="cta cta-launch grow"
              onClick={() => {
                setPhase('setup');
                setResult(null);
              }}
            >
              {t('app.trade.again')}
            </button>
            <button type="button" className="cta cta-secondary" onClick={() => setTab('home')}>
              {t('app.trade.to_menu')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
